<script setup lang="ts">
    import { computed, onMounted, ref } from 'vue'
    import VuePdfApp from 'vue3-pdf-app'
    import 'vue3-pdf-app/dist/icons/main.css'
    import { useStorage } from '@vueuse/core'
    import { useLayoutStore, useStudiengangStore } from '@/js/stores'

    const props = defineProps({
        modulHandbuch: {
            type: Object,
            required: true
        }
    })
    const layoutStore = useLayoutStore()
    const studiengangStore = useStudiengangStore()
    const activeTabState = useStorage('active-tab-pdf-output', 'tab-0')
    const tab = ref('tab-0')

    const token = computed(() => {
        return localStorage.getItem('lernleitstand-store-token')
    })

    const itemHeight = computed(() => {
        return `height: calc(100vh - ${layoutStore.appBarHeight}px - ${layoutStore.filterRowHeight}px - 140px)`
    })

    const pdfFileName = computed(() => {
        const studiengang = studiengangStore.getStudiengangById(props.modulHandbuch.studiengangId)
        const kurzName = studiengang?.kurzName ?? props.modulHandbuch.studiengangId
        const semester = props.modulHandbuch.semester ?? ''
        const updatedAt = props.modulHandbuch.updatedAt
            ? new Date(props.modulHandbuch.updatedAt).toISOString().slice(0, 10)
            : ''
        return [kurzName, semester, updatedAt].filter(Boolean).join('_')
    })

    const tabs = computed(() => {
        return [
            {
                key: 'tab-0',
                tabTitle: 'Komplett',
                tabIcons: ['mdi-file-multiple'],
                noAttachmentsMessage: false,
                iframeTitle: 'PDF-Ansicht des Modulhandbuches',
                iframeName: `${pdfFileName.value}_watermark.pdf`,
                iframeSrc: pdfSourceWithWatermark.value
            },
            {
                key: 'tab-1',
                tabTitle: 'Ohne Wasserzeichen',
                tabIcons: ['mdi-file-outline'],
                noAttachmentsMessage: false,
                iframeTitle: 'PDF-Ansicht des Modulhandbuches ohne Watermark',
                iframeName: `${pdfFileName.value}.pdf`,
                iframeSrc: pdfSourceWithoutWatermark.value
            }
        ]
    })

    const pdfSourceWithWatermark = computed(() => {
        let file = ''
        file += props.modulHandbuch
            ? `/pdf/${new Date(props.modulHandbuch.updatedAt).getTime()}/${props.modulHandbuch.modulHandbuchId}/mit/modulhandbuch.pdf?token=${token.value}`
            : ''

        return file
    })
    const pdfSourceWithoutWatermark = computed(() => {
        let file = ''
        file += props.modulHandbuch
            ? `/pdf/${new Date(props.modulHandbuch.updatedAt).getTime()}/${props.modulHandbuch.modulHandbuchId}/ohne/modulhandbuch.pdf?token=${token.value}`
            : ''

        return file
    })
    const activeTab = computed(() => {
        return activeTabState.value
    })
    const handleTabChange = (index: string) => (activeTabState.value = index)

    onMounted(() => {
        tab.value = activeTab.value
    })
</script>

<template>
    <div class="d-flex flex-column flex-wrap w-100">
        <v-tabs
            v-model="tab"
            align-tabs="center"
            bg-color=""
            stacked
            grow
            slider-color="primary"
            @update:model-value="handleTabChange"
        >
            <v-tab
                v-for="tab in tabs"
                :key="tab.key"
                :value="tab.key"
                :class="String(activeTab) === String(tab.key) ? 'text-primary' : 'text-grey'"
            >
                <v-icon v-for="icon in tab.tabIcons" :key="icon" class="mb-2" :icon="icon" />
                {{ tab.tabTitle }}
            </v-tab>
        </v-tabs>
        <v-tabs-window v-model="tab">
            <v-tabs-window-item v-for="tab in tabs" :key="tab.key" :value="tab.key">
                <div class="mt2" :style="itemHeight">
                    <div v-if="tab.noAttachmentsMessage">
                        {{ tab.noAttachmentsMessage }}
                    </div>
                    <vue-pdf-app
                        v-else-if="String(activeTab) === String(tab.key)"
                        style="height: 100%"
                        :pdf="tab.iframeSrc"
                        :file-name="tab.iframeName"
                    />
                </div>
            </v-tabs-window-item>
        </v-tabs-window>
    </div>
</template>

<style scoped>
    .pdf-app :deep(.toolbar) {
        z-index: 0;
    }

    :deep(.pdfViewer .page) {
        margin: 12px auto 24px auto !important;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35) !important;
        border: none !important;
    }
</style>
